import {
	Directive,
	DoCheck,
	EventEmitter,
	Input,
	IterableDiffer,
	IterableDiffers,
	OnChanges,
	Output,
	SimpleChange
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { orderBy } from 'lodash-es';
import { ReplaySubject } from "rxjs";
export interface SortEvent {
	sortBy: string | string[];
	sortOrder: string;
}
export interface PageEvent {
	activePage: number;
	rowsOnPage: number;
	dataLength: number;
}
export interface DataEvent {
	length: number;
}
@Directive({
	selector: "[mfData]",
	exportAs: "appDataTable",
	standalone: false
})
export class DataTableDirective implements OnChanges, DoCheck {
	private diff: IterableDiffer<any>;
	private mustRecalculateData = false;
	private groupCode: string;

	@Input() mfData: any[] = [];
	@Input() mfTableLength: number = null;
	@Input() mfSortBy: string | string[] = "";
	@Input() market: string;
	@Input() mfSortOrder: "asc" | "desc" = "asc";
	@Input() mfRowsOnPage = 1e4;
	@Input() mfActivePage = 1;

	@Output() mfSortByChange = new EventEmitter<string | string[]>();
	@Output() mfSortOrderChange = new EventEmitter<"asc" | "desc">();

	data: any[];
	onSortChange = new ReplaySubject<SortEvent>(1);
	@Output() onPageChange = new EventEmitter<PageEvent>();

	constructor(
		private differs: IterableDiffers,
		private translate: TranslateService
	) {
		this.diff = differs.find([]).create(null);
	}

	getSort(): SortEvent {
		return {
			sortBy: this.mfSortBy,
			sortOrder: this.mfSortOrder
		};
	}

	setSort(
		sortBy: string | string[],
		sortOrder: string,
		market: string,
		groupCode: string
	): void {
		if (this.mfSortBy !== sortBy || this.mfSortOrder !== sortOrder) {
			this.mfSortBy = sortBy;
			this.market = market;
			this.groupCode = groupCode;
			this.mfSortOrder = ["asc", "desc"].includes(sortOrder)
				? (sortOrder as "asc" | "desc")
				: "asc";
			this.mustRecalculateData = true;
			this.onSortChange.next({ sortBy, sortOrder });
			this.mfSortByChange.emit(this.mfSortBy);
			this.mfSortOrderChange.emit(this.mfSortOrder);
		}
	}

	getPage(): PageEvent {
		return {
			activePage: this.mfActivePage,
			rowsOnPage: this.mfRowsOnPage,
			dataLength: this.mfTableLength ? this.mfTableLength : this.mfData.length
		};
	}

	setPage(activePage: number, rowsOnPage: number): void {
		if (
			this.mfRowsOnPage !== rowsOnPage ||
			this.mfActivePage !== activePage
		) {
			this.mfActivePage =
				this.mfActivePage !== activePage
					? activePage
					: this.calculateNewActivePage(
						this.mfRowsOnPage,
						rowsOnPage
					);
			this.mfRowsOnPage = rowsOnPage;
			this.mustRecalculateData = true;
			this.onPageChange.emit({
				activePage: this.mfActivePage,
				rowsOnPage: this.mfRowsOnPage,
				dataLength: this.mfTableLength
					? this.mfTableLength
					: this.mfData
						? this.mfData.length
						: 0
			});
		}
	}

	ngOnChanges(changes: { [key: string]: SimpleChange }): any {
		if (changes["mfRowsOnPage"]) {
			// console.info("rows", changes["mfRowsOnPage"].currentValue);
			this.mfRowsOnPage = changes["mfRowsOnPage"].previousValue;
			this.setPage(
				this.mfActivePage,
				changes["mfRowsOnPage"].currentValue
			);
			this.mustRecalculateData = true;
		}
		if (changes["mfActivePage"]) {
			// console.info("page", changes["mfActivePage"].currentValue);
			this.setPage(
				changes["mfActivePage"].currentValue,
				this.mfRowsOnPage
			);
			this.mustRecalculateData = true;
		}
		if (changes["mfSortBy"] || changes["mfSortOrder"]) {
			if (!["asc", "desc"].includes(this.mfSortOrder)) {
				/* console.warn(
					"angular2-datatable: value for input mfSortOrder must be one of ['asc', 'desc'], but is: ",
					this.mfSortOrder
				); */
				this.mfSortOrder = "asc";
			}
			if (this.mfSortBy) {
				this.onSortChange.next({
					sortBy: this.mfSortBy,
					sortOrder: this.mfSortOrder
				});
			}
			this.mustRecalculateData = true;
		}
		if (changes["mfData"]) {
			this.mfData = changes["mfData"].currentValue || [];
			this.recalculatePage();
			this.mustRecalculateData = true;
		}
	}

	ngDoCheck(): any {
		const changes = this.diff.diff(this.mfData);
		if (changes) {
			this.recalculatePage();
			this.mustRecalculateData = true;
		}
		if (this.mustRecalculateData) {
			this.fillData();
			this.mustRecalculateData = false;
		}
	}

	private calculateNewActivePage(
		previousRowsOnPage: number,
		currentRowsOnPage: number
	): number {
		const firstRowOnPage = (this.mfActivePage - 1) * previousRowsOnPage + 1;
		return Math.ceil(firstRowOnPage / currentRowsOnPage);
	}

	private recalculatePage() {
		const lastPage = Math.ceil( /* length */(this.mfTableLength || this.mfData.length) / this.mfRowsOnPage);
		this.mfActivePage =
			(lastPage < this.mfActivePage ? lastPage : this.mfActivePage) || 1;

		this.onPageChange.emit({
			activePage: this.mfActivePage,
			rowsOnPage: this.mfRowsOnPage,
			dataLength: this.mfTableLength || this.mfData.length
		});
	}

	private fillData(): void {
		const offset = (this.mfActivePage - 1) * this.mfRowsOnPage;
		let data = this.mfData;
		const sortBy = this.mfSortBy;
		if (this.mfTableLength === null) {
			if (typeof sortBy === "string" || sortBy instanceof String) {
				data = orderBy(
					data,
					this.caseInsensitiveIteratee(<string>sortBy),
					[this.mfSortOrder]
				);
			} else {
				data = orderBy(data, sortBy, [this.mfSortOrder]);
			}
			data = data.slice(offset, offset + this.mfRowsOnPage);
		}
		this.data = data;
	}

	private caseInsensitiveIteratee =
		(sortBy: string) =>
			(row: any): any => {
				let value = row;
				for (const sortByProperty of sortBy.split(".")) {
					if (value) {
						value = value[sortByProperty];
					}
				}
				if (
					(value && typeof value === "string") ||
					value instanceof String
				) {
					if (this.market != null && this.groupCode != null) {
						const key = [this.market, this.groupCode, value].join("."),
							transValue = this.translate.instant(key);
						return key !== transValue ? transValue : value;
					}
					return value.toLowerCase();
				}
				return value;
			};
}
